import { z } from 'zod';
import { SchemaTimestamp } from '@axiom/types';

import { AccountSchema } from './account';
import { OpportunitySchema } from './opportunity';
import { CandidateOpportunitySchema } from './candidate-opportunities';
import { CandidateSchema } from './candidate';
import { HomeOfficeSchema } from './home-office';
import { PositionSchema } from './position';
import { BaseUserSchema } from './base-user';

export const OnboardingContactSchema = z.object({
  name: z.string(),
  jobTitle: z.string(),
  email: z.string(),
  phoneNumber: z.string(),
});

const AdditionalContactSchema = OnboardingContactSchema.extend({
  jobTitle: z.string().nullable(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
});

export type OnboardingContact = z.infer<typeof OnboardingContactSchema>;

export const OnboardingDetailsSchema = z.object({
  notes: z.string().nullable(),
  contact: OnboardingContactSchema.nullable(),
  updatedAt: SchemaTimestamp.nullable(),
  additionalContacts: z.array(AdditionalContactSchema).nullable(),
  displayTimeUnit: z.enum(['Hourly', 'Daily']).nullable(),
  displayTimeUnitUpdatedAt: SchemaTimestamp.nullable(),
});

export type OnboardingDetails = z.infer<typeof OnboardingDetailsSchema>;

export const OpportunityOnboardingSchema = OpportunitySchema.pick({
  jobName: true,
  currency: true,
  addressStreet: true,
  addressCity: true,
  addressState: true,
  addressZip: true,
  addressCountryCode: true,
  stage: true,
}).extend({
  id: z.string().uuid(),
  account: AccountSchema.pick({
    calculatedName: true,
  }),
  positions: z
    .array(
      PositionSchema.pick({
        id: true,
        startDate: true,
        billingHoursPerWeek: true,
        billingHoursPerDay: true,
        weeklyTimeUnit: true,
        reservedType: true,
        worksite: true,
      }).extend({
        candidateOpportunities: z
          .array(
            CandidateOpportunitySchema.pick({
              id: true,
              compensationType: true,
              proposedHourlyCompensation: true,
            }).extend({
              onboardingEmailSentAt: SchemaTimestamp.nullable(),
              onboardingDetails: OnboardingDetailsSchema.nullable(),
              candidate: CandidateSchema.pick({
                id: true,
                baseAnnualSalary: true,
                currencyType: true,
                addressState: true,
                addressZip: true,
                addressCountry: true,
                addressCountryCode: true,
                businessTitle: true,
                calculatedDisplayName: true,
                employmentStatus: true,
                employeeType: true,
                profileImageKey: true,
                profileImageName: true,
                profileStatus: true,
                settings: true,
              }).extend({
                ownerUser: BaseUserSchema.pick({
                  fullName: true,
                }).nullable(),
                homeOffice: HomeOfficeSchema.pick({
                  id: true,
                  name: true,
                }),
                barredLocations: z.array(
                  z.object({
                    name: z.string().nullable(),
                    location: z.string().nullable(),
                  })
                ),
              }),
              isFirstOnboarding: z.boolean(),
            })
          )
          .nullable(),
      })
    )
    .nullable(),
});

export type OpportunityOnboarding = z.infer<typeof OpportunityOnboardingSchema>;

export const OpportunityOnboardingPatchBodySchema = z.object({
  notes: z.string(),
  contact: AdditionalContactSchema,
  additionalContacts: z.array(AdditionalContactSchema),
  displayTimeUnit: z.enum(['Hourly', 'Daily']).nullish(),
});

export type OpportunityOnboardingPatchBody = z.infer<
  typeof OpportunityOnboardingPatchBodySchema
>;

export const CandidateOpportunityOnboardingSchema = OpportunityOnboardingSchema;

export type CandidateOpportunityOnboarding = z.infer<
  typeof CandidateOpportunityOnboardingSchema
>;
